import { ForwardedRef, forwardRef } from "react";
import AsyncSelect, { AsyncProps } from "react-select/async";
import type Select from "react-select/dist/declarations/src/Select";
import {
  GroupBase,
  ClassNamesConfig,
  components as RSComponents,
} from "react-select";
import { cva } from "class-variance-authority";
import { IoCloseOutline } from "react-icons/io5";
import { cn } from "../../../../utils/cn";
import { useDebounceLoadOptions } from "../../../../hooks/useDebounceLoadOptions";
import { ISelectOptions } from "../../../../types/inputs";
import {
  ACTIVE_TENANT,
  Tenant,
  TENANT_BORDER_CLASSES,
  TENANT_BG_HOVER_CLASSES,
} from "../../../../constants/tenant_colors";

const controlVariants = cva(
  "flex !min-h-[46px] w-full items-center rounded-[10px] border border-solid border-[#9d9d9d] bg-white transition-colors",
  {
    variants: {
      variant: {
        default: "pl-3 pr-2",
        search: "pl-3 pr-10",
      },
      isDisabled: {
        true: "cursor-not-allowed bg-gray-100 opacity-70",
        false: "",
      },
    },
    defaultVariants: { variant: "default", isDisabled: false },
  },
);

export type AsyncSimpleSelectV2Variant = "default" | "search";

type SelectClassNames<IsMulti extends boolean> = ClassNamesConfig<
  ISelectOptions,
  IsMulti,
  GroupBase<ISelectOptions>
>;

const buildDefaultClassNames = <IsMulti extends boolean>(
  variant: AsyncSimpleSelectV2Variant,
  tenant: Tenant,
): SelectClassNames<IsMulti> => ({
  control: (state) =>
    cn(
      controlVariants({ variant, isDisabled: state.isDisabled }),
      state.isFocused && TENANT_BORDER_CLASSES[tenant],
    ),
  valueContainer: () => "flex flex-1 flex-wrap items-center gap-1 py-1",
  input: () => "text-sm font-['Mulish'] font-semibold text-[#111]",
  placeholder: () => "text-sm font-['Mulish'] text-[#9d9d9d]",
  singleValue: () => "text-sm font-['Mulish'] font-semibold text-[#111]",
  indicatorsContainer: () => "flex items-center gap-1",
  indicatorSeparator: () => "hidden",
  dropdownIndicator: () => "cursor-pointer px-1",
  clearIndicator: () => "cursor-pointer px-1 text-[#111]",
  loadingIndicator: () => "px-1",
  menu: () =>
    "z-10 mt-1 overflow-hidden rounded-[10px] border border-solid border-[#e1e7e4] bg-white shadow-lg",
  menuList: () => "max-h-64 overflow-y-auto py-0",
  option: (state) =>
    // cn(
    //   "cursor-pointer border-b border-solid border-b-[rgba(103,122,118,0.4)] px-5 py-2.5 font-['Mulish'] text-sm font-semibold text-[#111] last:border-b-0",
    //   state.isFocused && TENANT_BG_HOVER_CLASSES[tenant],
    //   state.isSelected && "font-bold"
    // ),
    cn(
      "cursor-pointer px-5 py-2.5 font-['Mulish'] text-sm font-semibold text-[#111] last:border-b-0",
      state.isFocused && TENANT_BG_HOVER_CLASSES[tenant],
      state.isSelected && "font-bold",
    ),
  noOptionsMessage: () => "px-5 py-2.5 text-sm text-[#9d9d9d]",
  loadingMessage: () => "px-5 py-2.5 text-sm text-[#9d9d9d]",
  multiValue: () =>
    "flex items-center gap-1 rounded-md bg-[#f0f0f0] px-2 py-0.5",
  multiValueLabel: () => "text-xs font-['Mulish']",
  multiValueRemove: () => "cursor-pointer hover:text-red-500",
});

function mergeClassNames<IsMulti extends boolean>(
  base: SelectClassNames<IsMulti>,
  overrides?: SelectClassNames<IsMulti>,
): SelectClassNames<IsMulti> {
  const keys = new Set([
    ...Object.keys(base),
    ...Object.keys(overrides ?? {}),
  ]) as Set<keyof SelectClassNames<IsMulti>>;

  const merged = {} as Record<string, (state: any) => string>;

  keys.forEach((key) => {
    const baseFn = base[key] as ((state: any) => string) | undefined;
    const overrideFn = overrides?.[key] as ((state: any) => string) | undefined;
    merged[key as string] = (state) => cn(baseFn?.(state), overrideFn?.(state));
  });

  return merged as SelectClassNames<IsMulti>;
}

export interface IAsyncSimpleSelectV2Props<
  IsMulti extends boolean = false,
> extends Omit<
  AsyncProps<ISelectOptions, IsMulti, GroupBase<ISelectOptions>>,
  "unstyled" | "styles" | "variant"
> {
  label?: string;
  required?: boolean;
  /** "search" esconde a seta de abrir/fechar e mostra um ícone de lupa fixo. */
  variant?: AsyncSimpleSelectV2Variant;
}

function AsyncSimpleSelectV2Inner<IsMulti extends boolean = false>(
  {
    label,
    required,
    variant = "default",
    loadOptions,
    classNames: userClassNames,
    components: userComponents,
    className,
    ...rest
  }: IAsyncSimpleSelectV2Props<IsMulti>,
  ref: ForwardedRef<Select<ISelectOptions, IsMulti, GroupBase<ISelectOptions>>>,
) {
  const debouncedLoadOptions = useDebounceLoadOptions(loadOptions);

  const resolvedClassNames = mergeClassNames<IsMulti>(
    buildDefaultClassNames<IsMulti>(variant, ACTIVE_TENANT),
    userClassNames,
  );

  const DropdownIndicator = (indicatorProps: any) =>
    variant === "search" ? null : (
      <RSComponents.DropdownIndicator {...indicatorProps}>
        <img
          src="/assets/svgs/arrowDownSelect.svg"
          alt="ícone seta"
          className="cursor-pointer"
        />
      </RSComponents.DropdownIndicator>
    );

  const ClearIndicator = (indicatorProps: any) => (
    <RSComponents.ClearIndicator {...indicatorProps}>
      <IoCloseOutline size={18} className="cursor-pointer" />
    </RSComponents.ClearIndicator>
  );

  return (
    <div className={cn("relative font-['Mulish']", className)}>
      {label && (
        <label className="absolute -top-[9px] left-4 z-10 flex bg-gradient-to-t from-white from-50% to-transparent to-50% px-1 text-xs text-black/60">
          {label} {required && <span className="text-[#ed0000]">*</span>}
        </label>
      )}

      <AsyncSelect
        ref={ref}
        unstyled
        loadOptions={loadOptions ? debouncedLoadOptions : undefined}
        classNames={resolvedClassNames}
        components={{ DropdownIndicator, ClearIndicator, ...userComponents }}
        {...rest}
      />

      {variant === "search" && (
        <img
          src="/assets/svgs/icon-search.svg"
          alt="ícone lupa"
          className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2"
        />
      )}
    </div>
  );
}

/**
 * V2 do AsyncSimpleSelect: mesmo comportamento assíncrono/debounced de antes,
 * mas 100% Tailwind (via `unstyled` + `classNames` do react-select, em vez de
 * objetos de estilo JS) e altamente customizável — qualquer parte interna
 * (control, menu, option, ...) pode ser sobrescrita passando `classNames`, e
 * qualquer sub-componente (indicadores, etc.) pode ser trocado via `components`,
 * sem precisar editar este arquivo.
 */
export const AsyncSimpleSelectV2 = forwardRef(AsyncSimpleSelectV2Inner) as <
  IsMulti extends boolean = false,
>(
  props: IAsyncSimpleSelectV2Props<IsMulti> & {
    ref?: ForwardedRef<
      Select<ISelectOptions, IsMulti, GroupBase<ISelectOptions>>
    >;
  },
) => ReturnType<typeof AsyncSimpleSelectV2Inner>;
